import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell } from "@/components/page-shell";
import {
  ArticleBody,
  Clause,
  clauseToc,
  DotItem,
  DotList,
  EffectiveDate,
} from "@/components/guide-page";
import { absUrl } from "@/lib/site";

const EFFECTIVE_DATE = "July 20, 2026";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy | LoneStarMap" },
      {
        name: "description",
        content:
          "How LoneStarMap collects, uses, and protects the limited personal information you provide.",
      },
      { property: "og:title", content: "Privacy Policy" },
      {
        property: "og:description",
        content: "How LoneStarMap handles the limited personal information you provide.",
      },
      { property: "og:url", content: absUrl("/privacy") },
    ],
    links: [{ rel: "canonical", href: absUrl("/privacy") }],
  }),
  component: PrivacyPage,
});

const TITLES = [
  "Information we collect",
  "How we use information",
  "We do not sell your data",
  "Service providers (subprocessors)",
  "Cookies and tracking",
  "Data retention",
  "Children's privacy",
  "Your rights and choices",
  "Security",
  "International users",
  "Changes to this policy",
  "Contact",
] as const;

const LIST = "text-base leading-[1.7] text-foreground/85";

function PrivacyPage() {
  return (
    <PageShell
      title="Privacy Policy"
      intro='This Privacy Policy explains what information LoneStarMap.com (the "Site") collects, how we use it, and the choices you have. We try to collect as little as possible. The Site is a free, ad-free, educational resource. We do not sell your personal information.'
    >
      <EffectiveDate date={EFFECTIVE_DATE} />

      <ArticleBody toc={clauseToc(TITLES)}>
        <Clause titles={TITLES} n={1}>
          <h3 className="font-sans text-base font-semibold text-foreground">
            Information you give us
          </h3>
          <DotList className={LIST}>
            <DotItem>
              <strong>Contact form:</strong> your name, email address, and message.
            </DotItem>
          </DotList>
          <h3 className="pt-2 font-sans text-base font-semibold text-foreground">
            Information collected automatically
          </h3>
          <DotList className={LIST}>
            <DotItem>
              <strong>Standard server logs</strong> from our hosting provider (request time, URL,
              user-agent, IP) kept short-term for security and debugging.
            </DotItem>
            <DotItem>
              <strong>Analytics:</strong> Google Analytics 4 (page views, referrer, approximate
              location, device type). Google anonymizes IP addresses by default in GA4.
            </DotItem>
          </DotList>
        </Clause>

        <Clause titles={TITLES} n={2}>
          <DotList className={LIST}>
            <DotItem>Operate, maintain, and improve the Site.</DotItem>
            <DotItem>Respond to questions, feedback, and removal requests.</DotItem>
            <DotItem>Prevent spam, abuse, and security incidents.</DotItem>
            <DotItem>Comply with legal obligations.</DotItem>
          </DotList>
        </Clause>

        <Clause titles={TITLES} n={3}>
          <p>
            We do not sell, rent, or share your personal information with third parties for their
            marketing. We do not run advertising on the Site and we do not use advertising cookies.
          </p>
        </Clause>

        <Clause titles={TITLES} n={4}>
          <p>We use a small number of vendors to operate the Site:</p>
          <DotList className={LIST}>
            <DotItem>
              <strong>Lovable Cloud:</strong> hosting, database, and edge functions.
            </DotItem>
            <DotItem>
              <strong>Google Analytics 4:</strong> anonymized traffic analytics.
            </DotItem>
          </DotList>
          <p>
            These providers process data on our behalf under their own security and privacy terms.
            See our{" "}
            <Link to="/cookies" className="link">
              Cookie &amp; Tracking Notice
            </Link>{" "}
            for details.
          </p>
        </Clause>

        <Clause titles={TITLES} n={5}>
          <p>
            The Site uses a very small number of cookies and browser-storage items. See the{" "}
            <Link to="/cookies" className="link">
              Cookie &amp; Tracking Notice
            </Link>{" "}
            for the full list and opt-out instructions.
          </p>
        </Clause>

        <Clause titles={TITLES} n={6}>
          <DotList className={LIST}>
            <DotItem>
              <strong>Contact messages</strong> are
              retained until you ask us to delete them or until they are no longer needed.
            </DotItem>
            <DotItem>
              <strong>Server logs</strong> are retained for a short period for security and
              debugging.
            </DotItem>
          </DotList>
        </Clause>

        <Clause titles={TITLES} n={7}>
          <p>
            The Site is not directed to children under 13, and we do not knowingly collect personal
            information from children under 13. If you believe a child under 13 has provided
            information, please contact us and we will delete it promptly.
          </p>
        </Clause>

        <Clause titles={TITLES} n={8}>
          <p>
            You may request access to, correction of, or deletion of your personal information.
            To make a request, use our{" "}

            <Link to="/contact" className="link">
              contact form
            </Link>
            . Depending on where you live, you may have additional rights under laws such as the
            California Consumer Privacy Act (CCPA) or the EU/UK General Data Protection Regulation
            (GDPR). We will honor verifiable requests to the extent required by law.
          </p>
          <h3 className="pt-2 font-sans text-base font-semibold text-foreground">
            Do Not Sell or Share (CCPA)
          </h3>
          <p>
            We do not sell or share personal information as those terms are defined under the CCPA.
          </p>
        </Clause>

        <Clause titles={TITLES} n={9}>
          <p>
            We use reasonable administrative and technical measures to protect the limited
            information we hold, including row-level access controls in our database. No system is
            perfectly secure; you provide information at your own risk.
          </p>
        </Clause>

        <Clause titles={TITLES} n={10}>
          <p>
            The Site is operated from the United States. If you access it from outside the U.S.,
            your information will be transferred to and processed in the U.S., which may have
            different data-protection laws than your country.
          </p>
        </Clause>

        <Clause titles={TITLES} n={11}>
          <p>
            We may update this Privacy Policy from time to time. Material changes will be reflected
            in the effective date at the top of this page.
          </p>
        </Clause>

        <Clause titles={TITLES} n={12}>
          <p>
            For privacy questions or requests, use our{" "}
            <Link to="/contact" className="link">
              contact form
            </Link>
            .
          </p>
        </Clause>
      </ArticleBody>
    </PageShell>
  );
}
